import { GeneralResourceDetail, PracticeContentDataTypes, RecommendationsDetail } from '@cibo/core'
import {
  useProgramEngagementDetails,
  useUpdateProgramEngagementDetails,
} from '@cibo/programs/src/hooks/useProgramEngagementDetails'
import { FriendlyError, ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import Star from '@mui/icons-material/Star'
import { Alert, Grid, Skeleton, Stack, TablePagination, Typography, useTheme } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useEqipRecommendations,
  usePracticesInformation,
  useRecommendationsSummary,
} from '../../queries'
import { PracticeCard } from './PracticeCard'

export type PracticeCardsListProps<T extends GeneralResourceDetail = GeneralResourceDetail> =
  ResourceDetailFeatureTaskEditorProps<T>

export const PracticeCardsList = <T extends GeneralResourceDetail = GeneralResourceDetail>({
  userRole,
  ownerName,
  onError,
  onSuccess,
  onUpdating,
  resourceIds: [resourceId],
  engagement,
}: PracticeCardsListProps<T>) => {
  const {
    palette: { mode },
  } = useTheme()
  const { t } = useTranslation('@cibo/landmanager/PracticeCards')
  const updateEngagementDetails = useUpdateProgramEngagementDetails<T>()
  const details = useProgramEngagementDetails({
    resourceId,
    detailRequirements: [{ traitId: 'eqipRecommendedPracticesIntent', dataType: 'workflow' }],
  })
  const { data: singleRecommendation } = useEqipRecommendations(engagement.fields[0])
  const { data: summaryData, isLoading: isLoadingSummaryData } = useRecommendationsSummary({
    engagementId: resourceId,
  })
  const recommendations = summaryData?.recommendationSummary.recommendations || []

  const [page, setPage] = useState(0)
  const rowsPerPage = 8

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage)
  }

  const selectedCards = useMemo(() => {
    return (details?.data?.[0]?.result as string[]) || []
  }, [details.dataUpdatedAt])

  const onClickSelect = (practiceID: string) => {
    const newSelection = selectedCards.includes(practiceID)
      ? selectedCards.filter(id => id !== practiceID)
      : [...selectedCards, practiceID]

    onUpdating?.()
    updateEngagementDetails
      .mutateAsync({
        resourceId,
        details: [
          {
            traitId: 'eqipRecommendedPracticesIntent',
            result: newSelection.length ? newSelection : undefined,
          } as T,
        ],
      })
      .then(onSuccess)
      .catch(onError)
  }
  const { data, isLoading, isError } = usePracticesInformation()

  const allPracticesData = data?.items as PracticeContentDataTypes[]

  if (isLoading || isLoadingSummaryData) {
    return (
      <Grid columns={{ xs: 4, md: 8, lg: 12 }} spacing={2} container>
        <Grid xs={4} item>
          <Skeleton variant="rounded" height={150} />
        </Grid>
        <Grid xs={4} item>
          <Skeleton variant="rounded" height={150} />
        </Grid>
      </Grid>
    )
  }

  if (isError) {
    return <FriendlyError message={t('errorMessage')} />
  }

  // combine the practice recommendations & practice information datasets
  // if there's no matching, we will just keep the value as it is from practice information
  const cards = allPracticesData.map((allPractices: PracticeContentDataTypes) => {
    const matchingPractice = recommendations.find(
      (recommendedPractice: RecommendationsDetail) =>
        recommendedPractice.practiceCode === allPractices?.fields?.practiceId
    )
    if (matchingPractice) {
      return { ...allPractices, ...matchingPractice }
    } else {
      return allPractices
    }
  })

  cards.sort((cardA: PracticeContentDataTypes, cardB: PracticeContentDataTypes) => {
    return (cardB.totalPoints || 0) - (cardA.totalPoints || 0)
  })

  const startIndex = page * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const currentCards = cards.slice(startIndex, endIndex)
  const currentPage = page + 1
  const totalPage = Math.ceil(cards.length / rowsPerPage)

  return (
    <Stack spacing={3}>
      <Typography variant="h6" component="h2">
        {t('practiceSelectionTitle')}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {t('practiceSelectionSubtitle')}
      </Typography>

      <Alert
        icon={<Star sx={{ alignSelf: 'center' }} fontSize="large" color="info" />}
        severity="info"
        variant={mode === 'dark' ? 'filled' : 'standard'}
      >
        <Typography variant="body1" color="text.primary">
          {t('statePriorityTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {singleRecommendation?.addonText}
        </Typography>
      </Alert>

      <Grid columns={{ xs: 4, md: 8, lg: 12 }} spacing={3} container>
        {currentCards?.map((card: PracticeContentDataTypes, index: number) => (
          <Grid
            xs={6}
            item
            style={{ paddingLeft: 0, paddingTop: 0, paddingRight: 24, paddingBottom: 24 }}
            key={card.fields.name}
          >
            <PracticeCard
              title={card.fields?.name}
              subTitle={card?.fields?.definition}
              recommendedTag={card?.tags}
              practiceDetails={card?.fields}
              resourceConcernList={card?.resourceConcernComponent}
              userRole={userRole}
              ownerName={ownerName}
              isSelected={selectedCards.includes(card.fields.practiceId)}
              onClickSelect={() => onClickSelect(card.fields.practiceId)}
            />
          </Grid>
        ))}
      </Grid>
      <Stack
        direction="row"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" color="text.primary">
          {t('pageIndex', { currentPage, totalPage })}
        </Typography>
        <TablePagination
          component="div"
          count={cards.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[rowsPerPage]}
          sx={{
            '& .MuiTablePagination-displayedRows': {
              display: 'none',
            },
          }}
        />
      </Stack>
    </Stack>
  )
}
